/* eslint-disable */
import {
  Box,
  Button,
  Checkbox,
  Code,
  Flex,
  Icon,
  IconButton,
  Select,
  Spacer,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card";
import { SearchBar } from "components/searchBar/SearchBar";
import React, { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel
} from '@tanstack/react-table'

import { MdArrowDropDown, MdArrowDropUp, MdArrowUpward, MdMenu, MdRefresh, MdSwapVert } from "react-icons/md";
import { smartSort } from "lib/sort";
import { tokenFilter } from "lib/utils";

// Every row has the same height (room for 3 lines of text), so the pager under the table
// stays where it is from page to page. Longer content is cut off; open the item to read it.
const ROW_HEIGHT = '76px'
const CELL_MAX_HEIGHT = '60px'
// A short last page is filled with empty rows up to this page size, so Previous / Next do
// not jump on it either. Larger pages would add screens of empty space.
const FILL_UP_TO = 20

export default function DataTableNew(props) {
  // `context`: extra data for the cells and for `meta.searchText` (e.g. where content is used);
  // keep the object the same between renders, a new one makes the search texts be built again
  // `totalCount`: how many items there are in all, before the filters of the page (the drop-downs
  // above the table) are applied. "Showing 379 / 7055" = shown now / all of them. Without it the
  // number of rows handed in is used.
  // `resetPageKey`: changes when the user changes a filter above the table; the table then goes back to
  // the first page. (Rows that only get refreshed by live data keep the page the user is on.)
  const { columns, data, actions, refresh, loading, filters, enableSelection, bulkActions, defaultSort, context, totalCount, resetPageKey } = props

  const [globalFilter, setGlobalFilter] = useState('')
  // The starting order, e.g. [{ id: 'name', desc: false }]; a click on a column header changes it
  const [sorting, setSorting] = useState(() => defaultSort || [])
  const [rowSelection, setRowSelection] = useState({})

  // Subtle background for every second row (zebra striping) so a row is easier
  // to follow from left to right.
  const stripeBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100')
  // The column names stay on top while the rows scroll, so they need a solid background
  const headBg = useColorModeValue('white', 'navy.800')
  const [pagination, setPagination] = useState({
    pageIndex: 0, // Initial page index
    pageSize: 10, // Initial page size
  });

  // A leading checkbox column, prepended only when selection is enabled so
  // the other tables that share this component are unaffected.
  const selectionColumn = {
    id: '__select',
    size: 20,
    header: ({ table }) => (
      <Checkbox
        isChecked={table.getIsAllPageRowsSelected()}
        isIndeterminate={table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected()}
        onChange={table.getToggleAllPageRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        isChecked={row.getIsSelected()}
        isIndeterminate={row.getIsSomeSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
  }

  const tableColumns = useMemo(
    () => enableSelection ? [selectionColumn, ...columns] : columns,
    [columns, enableSelection]
  )

  // The search looks at every column that shows something, as it is shown. A column describes
  // its text with `meta: { searchText: (item) => '...' }` when the value is displayed
  // differently (a duration "1m 5s", a date "October 8, 2026 9:46 AM", "N/A" for an empty
  // field); without it the plain value is used. Columns that only hold buttons or a picture
  // (no value) add nothing, and the ID is not shown, so it is not searched.
  // The text of a row is built once per set of rows, not on every keystroke.
  const searchTexts = useMemo(() => new WeakMap(), [data, tableColumns, context])

  // Every row is keyed by the id of its record, so React keeps a row's cells (an open form, a
  // tooltip) with THAT record when the list changes by itself (live data: someone else deletes or
  // adds a record). Keyed by position, an open "Update" form would silently switch to the next
  // record. A record without an id, or an id that is twice in the list, falls back to its position.
  const rowIds = useMemo(() => {
    const seen = new Set()
    return new Map(data.map((record, index) => {
      const id = record?.id
      const key = typeof id === 'string' && id && !seen.has(id) ? id : `#${index}`
      seen.add(key)
      return [record, key]
    }))
  }, [data])

  const rowSearchText = (row) => {
    let text = searchTexts.get(row.original)
    if (text === undefined) {
      text = row.getAllCells().map(cell => {
        const searchText = cell.column.columnDef.meta?.searchText
        if (searchText) return searchText(row.original, context)
        const value = cell.getValue()
        return value === undefined || value === null || typeof value === 'object' ? '' : String(value)
      }).join(' ')
      searchTexts.set(row.original, text)
    }
    return text
  }

  const tokenMatchFilter = (row, columnId, value) => {
    if (!value) return true
    return tokenFilter(rowSearchText(row), value)
  }

  const table = useReactTable({
    data: data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: tokenMatchFilter,
    // The search reads the whole row at once, so ONE column is enough to carry it (the first
    // one with a value); otherwise the library would run it again for every column
    getColumnCanGlobalFilter: column => column.id === table.getAllLeafColumns().find(c => !!c.accessorFn)?.id,
    // Text in the Serbian alphabet, numbers as numbers, empty values always at the end
    defaultColumn: { sortingFn: smartSort, sortUndefined: 'last' },
    sortDescFirst: false,
    getRowId: (row, index) => rowIds.get(row) ?? `#${index}`,
    enableRowSelection: !!enableSelection,
    // The lists follow the database live, so the rows change on their own (somebody uploads content).
    // That must not throw the user back to page 1; the page is reset by the effect below instead.
    autoResetPageIndex: false,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    state: {
      globalFilter,
      sorting,
      pagination,
      rowSelection
    },
    context: {
      ...context,
      refresh
    }
  })

  // Back to the first page when the user changes what is shown: search, order, the filters above
  // the table. A page past the end (rows disappeared) moves to the last one.
  useEffect(() => {
    if (table.getState().pagination.pageIndex !== 0) table.setPageIndex(0)
  }, [globalFilter, sorting, resetPageKey])
  const lastPage = Math.max(0, table.getPageCount() - 1)
  useEffect(() => {
    if (table.getState().pagination.pageIndex > lastPage) table.setPageIndex(lastPage)
  }, [lastPage, pagination.pageIndex])

  const selectedRows = enableSelection ? table.getSelectedRowModel().rows.map(r => r.original) : []
  const clearSelection = () => table.resetRowSelection()

  // Rows missing on a short page (the last one, or after a search) up to the page size
  const rowCount = table.getRowModel().rows.length
  const pageSize = table.getState().pagination.pageSize
  const fillerRows = rowCount === 0 ? 0 : Math.max(0, Math.min(pageSize, FILL_UP_TO) - rowCount)

  return (
    <Card
      direction='column'
      w='100%'
      px='0px'
      flex='1'
      minH='360px'
      overflow='hidden'>
      {/* On a phone the controls wrap onto several lines instead of running off the screen */}
      <Flex px={{ base: '12px', md: '25px' }} justify='space-between' mb='20px' align='center' wrap='wrap' gap={3} flexShrink={0}>
        <Stack direction='row' align='center' wrap='wrap' spacing={0} gap={2} minW={0} maxW='100%'>
          <SearchBar
            borderRadius="30px"
            onChange={e => {
              setGlobalFilter(e.target.value || '')
            }}
          />
          { filters }
          { enableSelection && selectedRows.length > 0 && (
            <Stack direction='row' align='center' spacing={2}>
              <Text fontSize='sm' color='gray.500'>{selectedRows.length} selected</Text>
              { bulkActions && bulkActions(selectedRows, clearSelection) }
            </Stack>
          )}
        </Stack>
        <Spacer display={{ base: 'none', md: 'block' }} />
        <Stack direction='row' spacing={2} wrap='wrap' gap={2}>
          <Select
            w='unset'
            style={{borderRadius: '30px' }}
            size="sm"
            display='flex'
            alignItems='center'
            defaultValue='10'
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            >
            <option value='10'>10</option>
            <option value='20'>20</option>
            <option value='50'>50</option>
            <option value='100'>100</option>
            <option value='200'>200</option>
          </Select>
          <IconButton icon={<Icon as={MdRefresh} />} colorScheme='brand' variant='ghost' size='sm' borderRadius={30} onClick={refresh}></IconButton>
          { actions }
        </Stack>
      </Flex>
      {/* Only this box scrolls. minW: on a narrow screen the table scrolls sideways instead of
          squeezing the columns. Separate borders, so the header keeps its line while sticking. */}
      <Box flex='1' minH='220px' overflow='auto' mb='8px'>
      <Table variant='simple' color='gray.500' minW='900px' sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
        <Thead>
          {table.getHeaderGroups().map(headerGroup => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <Th key={header.id}
                  position='sticky' top='0' zIndex={2} bg={headBg}
                  width={header.getSize()}
                  cursor={header.column.getCanSort() ? 'pointer' : 'auto'}
                  onClick={header.column.getToggleSortingHandler()}
                  pl={index === 0 ? 8 : 4}
                  pr='0' fontSize='xs' mb='2' borderColor='gray.200'>
                    <Box display='flex'>
                      <Text>
                        {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </Text>
                      {header.column.getCanSort() && <Icon as={{ desc: MdArrowDropDown, asc: MdArrowDropUp}[header.column.getIsSorted()] || MdSwapVert} ml='1' width='16px' height='16px' />}
                    </Box>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          { loading && (
            <Tr>
              <Td colSpan={table.getAllColumns().length} textAlign='center'>
                <Spinner />
              </Td>
            </Tr>
          )}
          {table.getRowModel().rows.map((row, ri) => (
            <Tr key={row.id} bg={ri % 2 === 1 ? stripeBg : undefined} h={ROW_HEIGHT}>
              {row.getVisibleCells().map((cell, ci) => (
                <Td key={cell.id} fontSize='sm' pl={ci === 0 ? 8 : 4} pr='0' py={0} verticalAlign='middle'>
                  <Box maxH={CELL_MAX_HEIGHT} overflow='hidden'>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Box>
                </Td>
              ))}
            </Tr>
          ))}
          {/* Empty rows keep the table (and the pager below it) the same height on the last page */}
          {!loading && Array.from({ length: fillerRows }, (_, fi) => (
            <Tr key={'filler-' + fi} h={ROW_HEIGHT} bg={(rowCount + fi) % 2 === 1 ? stripeBg : undefined} aria-hidden='true'>
              <Td colSpan={table.getAllColumns().length} py={0} borderColor='transparent' />
            </Tr>
          ))}
        </Tbody>
      </Table>
      </Box>
      <Flex px={{ base: '12px', md: '25px' }} justify='space-between' mb='20px' align='center' wrap='wrap' gap={3} flexShrink={0}>
      <Stack direction='row' spacing={4} align='center'>
        <Box minW={{ base: 'auto', md: '200px' }} verticalAlign='center'>
        <Text fontSize='sm' textColor='gray'>Showing {table.getSortedRowModel().rows.length} / {Math.max(totalCount ?? 0, data.length)} items</Text>
        </Box>
      </Stack>

      <Stack direction='row' spacing={4} align='center'>
        <Box minW='100px' whiteSpace='nowrap'>
          <Text fontSize='sm' textColor='gray'>Page {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}</Text>
        </Box>
        <Button size='sm'
          onClick={() => table.previousPage()}
          disabled={!table.getCanPreviousPage()}>
          Previous
        </Button>
        <Button size='sm' 
          onClick={() => table.nextPage()}
          disabled={!table.getCanNextPage()}>
          Next
        </Button>
      </Stack>

      </Flex>
    </Card>
  );
}